import React, { useRef } from 'react';
import { Tooltip, Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    CustomerSectionBgImg, OilIndia,
  oilIndia,
  ONGC,
  BPCL,
  BD,
  OMC,
  CoalIndia,
  NCL,
  NHPC,
  GE,
  GELL,
  SECL,
  EY,
  Accenture
} from '../constant';

// Styled Components
const TooltipContent = styled(Box)({
  background: "#07329F",
  padding: "30px",
  borderRadius: "12px",
  color: "#FFFFFF",
  textAlign: "left",
  fontSize: "14px",
  fontWeight: "500",
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
});

const MainContainer = styled(Box)({
  minHeight: "500px",
  background: `url(${CustomerSectionBgImg}) center/cover no-repeat`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  overflow: "hidden",
  "@media (max-width: 960px)": { // Tablet and below
    minHeight: "100%",
  },
});

const ContentWrapper = styled(Box)({
  position: 'relative',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  zIndex: 1,
  margin: '0 auto'
});

const HighlightText = styled('span')({
  color: '#6B46FF',
  background: 'linear-gradient(262.41deg, #1DA1F2 -8.32%, #6843EC 93.3%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
});

const CompaniesWrapper = styled(Box)({
  width: "100%",
  overflow: "hidden",
  position: "relative",
  whiteSpace: "nowrap",
});


const CompanyBox = styled(Box)({
  width: "300px",
  minWidth: "300px",
  height: "100px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "0.5px solid #8C8CC2",
  padding: "5px",

  "& img": {
    maxWidth: "100%",
    maxHeight: "80px",
    width: "auto",
    height: "auto",
    objectFit: "contain",
    display: "block",
  },

  "@media (max-width: 900px)": {
    width: "260px",
    minWidth: "260px",
    height: "100px",

    "& img": {
      maxHeight: "60px",
    },
  },
});

const MarqueeTrack = styled(Box)({
  display: "flex",
  width: "max-content",
  animation: "marquee 35s linear infinite",

  "@keyframes marquee": {
    "0%": {
      transform: "translateX(0)",
    },
    "100%": {
      transform: "translateX(-50%)",
    },
  },

  "&:hover": {
    animationPlayState: "paused",
  },
});

const CustomersAndUsers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const scrollRef = useRef(null);

  // Dynamic visible items based on screen size
  const visibleItems = isMobile ? 2 : isTablet ? 3 : 5;

  const companies = [
    {
      Component: oilIndia,
      name: "Oil India",
      title:
        "Transformed EHS Workflows & Delivered ESG Intelligence.",
    },
    {
      Component: ONGC,
      name: "ONGC",
      title:
        "Predictive Intelligence for Remote Asset Operations to enhance production.",
    },
    {
      Component: BPCL,
      name: "Bharat Petroleum",
      title:
        "Connected Man and Machines Intelligence for Time & Cost Performance",
    },
    {
      Component: OMC,
      name: "Odisha Mining",
      title:
        "Integrated EHS Intelligence System across entire mining value chain",
    },
    {
      Component: CoalIndia,
      name: "Coal India",
      title:
        "System Integration and Business Intelligence across 15 digital use cases",
    },
    {
      Component: NCL,
      name: "Northern CoalFields",
      title:
        "Safety Intelligence, Coal Quality Workflows, Environment Monitoring",
    },
    {
      Component: SECL,
      name: "South Eastern Coalfields",
      title:
        "Transport Management, Safety Intelligence, Contractor Performance",
    },
    {
      Component: GE,
      name: "GE",
      title:
        "Enterprise Risk Management workflow aggregation across 20+ subsidiaries",
    },
    {
      Component: Accenture,
      name: "Accenture",
      title:
        "Collaboration for delivering digital transformation in mining, oil & gas client",
    },
    {
      Component: EY,
      name: "EY",
      title:
        "Digital Transformation Intelligence for transforming Safety & Work Environment",
    },
    {
      Component: NHPC,
      name: "NHPC",
      title:
        "Enterprise risk intelligence to upgrade mitigation plans",
    },
    {
      Component: GELL,
      name: "GAIL India",
      title:
        "Operational risk intelligence across key petrochemical plants",
    },
    {
      Component: BD,
      name: "Bharat Dynamics",
      title:
        "ESG Reporting and Intelligence for critical defence manufacturing operations",
    },
  ];

  const handleMouseOver = (e) => {
    if (scrollRef.current) {
      const { clientWidth, scrollWidth, scrollLeft } = scrollRef.current;
      const threshold = clientWidth * 0.8;

      if (scrollLeft + clientWidth >= scrollWidth - threshold) {
        scrollRef.current.scrollLeft += 50; // Smooth scroll on hover at the end
      }
    }
  };

  return (
    <MainContainer>
      <ContentWrapper>
        <Box textAlign="center">
          <Typography
            variant="h3"
            component="h2"
            sx={{
              fontSize: { xs: "28px", md: "38px", lg: "42px" },
              fontWeight: 700,
              lineHeight: 1.2,
              mb: 5
            }}
          >
            <HighlightText>Trusted by Enterprises Operating at Scale</HighlightText>
          </Typography>
          <Typography
            color="#8C8CC2"
            sx={{
              fontSize: { xs: '14px', md: '18px' },
              mx: 'auto',
              mb: { xs: 5, md: 10 }
            }}
          >
            Enabling organizations to operate with greater efficiency, visibility, and confidence across critical business functions.
          </Typography>
        </Box>

        {/* Companies Horizontal Scroll */}
        <CompaniesWrapper>
          <MarqueeTrack>
            {[...companies, ...companies].map(
              ({ Component, name, title }, index) => (
                <Tooltip
                  key={index}
                  title={
                    <TooltipContent>
                      <Typography variant="h6" fontWeight="bold">
                        {name}
                      </Typography>
                      <Typography variant="body2">
                        {title}
                      </Typography>
                    </TooltipContent>
                  }
                  placement="top"
                  arrow
                >
                  <CompanyBox>
                    <img
                      src={Component}
                      alt={name}
                    />
                  </CompanyBox>
                </Tooltip>
              )
            )}
          </MarqueeTrack>
        </CompaniesWrapper>
      </ContentWrapper>
    </MainContainer>
  );
};

export default CustomersAndUsers;
